import { useEffect, useState } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

const ALLOWED_DOMAINS = ["babylux.com.tw", "shintaistrategy.com"];

export function isAllowedEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  const domain = email.toLowerCase().split("@")[1];
  return ALLOWED_DOMAINS.includes(domain);
}

interface AdminAuthState {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isViewer: boolean;
  loading: boolean;
}

export function useAdminAuth(): AdminAuthState {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Register listener first
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s) {
        setIsAdmin(false);
        setIsViewer(false);
        setLoading(false);
        return;
      }
      // Enforce domain gate
      if (!isAllowedEmail(s.user.email)) {
        supabase.auth.signOut();
        setIsAdmin(false);
        setIsViewer(false);
        setLoading(false);
        return;
      }
      // Defer role query
      setTimeout(async () => {
        const { data } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", s.user.id);
        const roles = (data ?? []).map((r) => r.role);
        setIsAdmin(roles.includes("admin"));
        setIsViewer(roles.includes("viewer"));
        setLoading(false);
      }, 0);
    });

    // Then get initial session
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) setLoading(false);
      // onAuthStateChange fires with INITIAL_SESSION anyway
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  return {
    session,
    user: session?.user ?? null,
    isAdmin,
    isViewer,
    loading,
  };
}
